import {
    Box as MuiBox,
    ButtonBase,
    Dialog,
    Divider,
    FormControl,
    FormLabel,
    LinearProgress,
    MenuItem,
    Select,
    Tooltip,
    Typography,
} from "@mui/material";
import { type EnvironmentDescription, getDeviceProfile, GPUTier } from "@novorender/api";
import { ChevronRight, Download, Home, Trash } from "@procore/core-icons";
import { Box, Button, ConfirmModal, Modal, Spinner, Typography as ProcoreTypography } from "@procore/core-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { getModelHomeView, saveModelHomeViewpoint } from "apis/procore/bimModelHomeView";
import { getBimModelRevisions } from "apis/procore/bimModelRevision";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { ToggleWithText } from "components/toggleWithText";
import { FeatureToggle } from "config/featureToggles";
import { StorageKey } from "config/storage";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { usePanelPickerOverlay } from "contexts/panelPickerOverlay";
import { selectCurrentDrawing } from "features/miniMap/minimapSlice";
import { offlineActions, selectOfflineAction, selectOfflineScenes } from "features/offline/offlineSlice";
import { renderActions, selectAdvanced, selectBackground, selectDeviceProfile } from "features/render";
import { SelectionResolution } from "features/render/types";
import { viewpointCameraToCadCamera } from "features/render/utils";
import { UnitSystem } from "features/uom/spec";
import { useGetViewpoint } from "features/viewpoints/useGetViewpoint";
import { useSceneId } from "hooks/useSceneId";
import {
    explorerActions,
    selectIsAxisSphereVisible,
    selectIsJoysticksVisible,
    selectIsMiniMapVisible,
    selectIsOnline,
    selectIsProcoreMobileDesign,
    selectProcoreCompanyId,
    selectProcoreContext,
    selectProcoreProjectId,
    selectProjectV2Info,
    selectSelectionResolution,
    selectUnitSystem,
} from "slices/explorer";
import { selectIsAdminScene } from "slices/explorer/selectors";
import { persistFeatureToggle, useFeatureToggle } from "slices/featureToggles";
import { telemetry } from "telemetry";
import { getAsyncStateData } from "types/misc";
import { formatFileSizeMetric } from "utils/misc";
import { saveToStorage } from "utils/storage";

import { type PickerOption, SettingsPickerView } from "./SettingsPickerView";

type PickerKind = "unitSystem" | "performance" | "selectionResolution";

interface SettingsContentProps {
    useSlideInPicker?: boolean;
}

function getTierKey(tier: GPUTier): string {
    switch (tier) {
        case 0:
            return "low";
        case 1:
            return "medium";
        case 2:
            return "high";
        case 3:
            return "ultra";
        default:
            return "medium";
    }
}

const DEVICE_PROFILES = [0, 1, 2, 3].map((tier) => getDeviceProfile(tier as GPUTier));

const SELECTION_RESOLUTION_OPTIONS = [
    { value: SelectionResolution.FirstObject, key: "firstObject" },
    { value: SelectionResolution.LastObject, key: "lastObject" },
] as const;

/**
 * Model Download section for the Settings panel.
 * Shows Download (fullSync) or Remove from Device based on sync status,
 * with a progress bar while scanning/downloading. Status is driven by
 * the SDK's offline state machine (see useHandleOffline.ts).
 */
function ModelDownloadSection({ parentSceneId }: { parentSceneId: string }) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const scenes = useAppSelector(selectOfflineScenes);
    const action = useAppSelector(selectOfflineAction);
    const isOnline = useAppSelector(selectIsOnline);
    const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);

    const scene = scenes[parentSceneId];
    const status = scene?.status;
    const isSynced = status === "synchronized";
    // True while an offline action is dispatched or the SDK reports in-progress status.
    const isWorking =
        action !== undefined || status === "synchronizing" || status === "scanning" || status === "downloading";
    const progressPercent = scene?.progress ? Number.parseInt(scene.progress, 10) : NaN;
    const hasDeterminateProgress = Number.isFinite(progressPercent);

    const handleDownload = () => {
        dispatch(offlineActions.setAction({ action: "fullSync" }));
    };

    const handleRemoveClick = () => {
        setConfirmRemoveOpen(true);
    };

    const handleConfirmRemove = () => {
        setConfirmRemoveOpen(false);
        dispatch(offlineActions.setAction({ action: "delete", id: parentSceneId }));
    };

    const handleCancelRemove = () => {
        setConfirmRemoveOpen(false);
    };

    return (
        <Box display="flex" flexDirection="column" gap="0.5rem">
            <FormLabel sx={{ fontWeight: 600, color: "text.primary" }}>{t("modelDownload.title")}</FormLabel>
            <Typography variant="body1" color="text.secondary">
                {isSynced
                    ? t("modelDownload.syncedMessage", {
                          size: scene?.size ? formatFileSizeMetric(scene.size) : "",
                      })
                    : t("modelDownload.description")}
            </Typography>
            {isWorking && (
                <MuiBox sx={{ my: 0.5 }}>
                    <LinearProgress
                        variant={hasDeterminateProgress ? "determinate" : "indeterminate"}
                        value={hasDeterminateProgress ? progressPercent : undefined}
                    />
                </MuiBox>
            )}
            <Box>
                {isSynced ? (
                    <Button
                        variant="secondary"
                        icon={<Trash />}
                        disabled={isWorking}
                        onClick={handleRemoveClick}
                        data-testid="settings-model-remove-button"
                    >
                        {t("modelDownload.remove")}
                    </Button>
                ) : (
                    <Button
                        variant="secondary"
                        icon={<Download />}
                        disabled={!isOnline || isWorking}
                        onClick={handleDownload}
                        data-testid="settings-model-download-button"
                    >
                        {action?.action === "fullSync" ? t("modelDownload.downloading") : t("modelDownload.download")}
                    </Button>
                )}
            </Box>

            {/* iOS-style native alert per Figma. Custom because the section is mobile-only. */}
            <Dialog
                open={confirmRemoveOpen}
                onClose={handleCancelRemove}
                // Disable MUI's default size container so PaperProps width wins.
                maxWidth={false}
                PaperProps={{
                    sx: {
                        width: 244,
                        minWidth: 244,
                        height: 230,
                        borderRadius: "14px",
                        m: 0,
                        // Figma: --native-ios-materials-regular
                        backgroundColor: "rgba(248, 248, 248, 0.82)",
                        backdropFilter: "blur(20px)",
                        WebkitBackdropFilter: "blur(20px)",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                    },
                }}
                BackdropProps={{
                    // Figma: --background-overlay
                    sx: { backgroundColor: "rgba(0, 0, 0, 0.8)" },
                }}
            >
                {/* Text container per Figma; top-anchored so padding-top is the visible top spacing. */}
                <MuiBox
                    sx={{
                        height: 140,
                        boxSizing: "border-box",
                        pt: "19px",
                        pr: "16px",
                        pb: "17px",
                        pl: "16px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        textAlign: "center",
                        gap: "2px",
                    }}
                >
                    <ProcoreTypography intent="body" weight="semibold">
                        {t("modelDownload.confirmRemoveTitle")}
                    </ProcoreTypography>
                    <ProcoreTypography intent="small">{t("modelDownload.confirmRemoveBody")}</ProcoreTypography>
                </MuiBox>
                <Divider />
                {/* Buttons: 44px each, transparent background, iOS-blue text (Remove semibold). */}
                <ButtonBase
                    onClick={handleConfirmRemove}
                    data-testid="settings-model-remove-confirm-button"
                    sx={{ height: 44, width: "100%" }}
                >
                    <ProcoreTypography intent="body" weight="semibold" style={{ color: "rgba(0, 122, 255, 1)" }}>
                        {t("modelDownload.confirmRemoveAction")}
                    </ProcoreTypography>
                </ButtonBase>
                <Divider />
                <ButtonBase
                    onClick={handleCancelRemove}
                    data-testid="settings-model-remove-cancel-button"
                    sx={{ height: 44, width: "100%" }}
                >
                    <ProcoreTypography intent="body" style={{ color: "rgba(0, 122, 255, 1)" }}>
                        {t("cancel")}
                    </ProcoreTypography>
                </ButtonBase>
            </Dialog>
        </Box>
    );
}

const FRAMERATE_TARGET_OPTIONS = ["30", "60"] as const;
type FramerateTargetValue = (typeof FRAMERATE_TARGET_OPTIONS)[number];

const sectionLabelSx = { fontWeight: 600, color: "text.primary", mb: 1 } as const;

const pickerRowSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0.625rem 0.75rem",
    borderRadius: "4px",
    border: "1px solid rgba(0, 0, 0, 0.23)",
    backgroundColor: "white",
    cursor: "pointer",
    width: "100%",
    textAlign: "left" as const,
    color: "text.primary",
    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
    "&:focus-visible": { outline: "2px solid #1976d2", outlineOffset: "-2px" },
} as const;

interface PickerRowProps {
    label: string;
    valueLabel: string;
    onClick: () => void;
    disabled?: boolean;
    testId?: string;
}

function PickerRow({ label, valueLabel, onClick, disabled, testId }: PickerRowProps) {
    return (
        <FormControl fullWidth size="small">
            <FormLabel sx={sectionLabelSx}>{label}</FormLabel>
            <MuiBox
                component="button"
                type="button"
                onClick={disabled ? undefined : onClick}
                sx={{
                    ...pickerRowSx,
                    ...(disabled ? { opacity: 0.5, cursor: "default" } : {}),
                }}
                data-testid={testId}
            >
                <Typography variant="body2" sx={{ color: "text.primary" }}>
                    {valueLabel}
                </Typography>
                <ChevronRight />
            </MuiBox>
        </FormControl>
    );
}

export function SettingsContent({ useSlideInPicker = false }: SettingsContentProps = {}) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const unitSystem = useAppSelector(selectUnitSystem);
    const deviceProfile = useAppSelector(selectDeviceProfile);
    const selectionResolution = useAppSelector(selectSelectionResolution);
    const advanced = useAppSelector(selectAdvanced);
    const background = useAppSelector(selectBackground);
    const companyId = useAppSelector(selectProcoreCompanyId);
    const projectId = useAppSelector(selectProcoreProjectId);
    const procoreContext = useAppSelector(selectProcoreContext);
    const isMiniMapVisible = useAppSelector(selectIsMiniMapVisible);
    const isAxisSphereVisible = useAppSelector(selectIsAxisSphereVisible);
    const isProcoreMobileDesign = useAppSelector(selectIsProcoreMobileDesign);
    const isJoysticksVisible = useAppSelector(selectIsJoysticksVisible);
    const isUpdateHomeViewEnabled = useFeatureToggle(FeatureToggle.UpdateHomeView);
    const isAdminScene = useAppSelector(selectIsAdminScene);
    const isOnline = useAppSelector(selectIsOnline);
    const miniMapAvailable = useAppSelector(selectCurrentDrawing) !== null;
    const sceneId = useSceneId();
    // Offline scenes are keyed by parentSceneId (projectV2 id) in useHandleOffline.
    const parentSceneId = useAppSelector(selectProjectV2Info)?.id;
    const {
        state: { view },
    } = useExplorerGlobals();
    const getViewpoint = useGetViewpoint();
    const [modalOpen, setModalOpen] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);

    const pickerOverlay = usePanelPickerOverlay();

    const currentTier = deviceProfile.tier;
    const isAntiAliasingEnabled = advanced.msaa.enabled;
    const framerateTarget = String(deviceProfile.framerateTarget) as FramerateTargetValue;
    const environments = useMemo<EnvironmentDescription[]>(
        () => getAsyncStateData(background.environments) ?? [],
        [background.environments],
    );

    // Option lists (stable across renders for use in picker callbacks)
    const unitSystemOptions = useMemo<PickerOption<UnitSystem>[]>(
        () => [
            { value: UnitSystem.imperial, label: t("imperial") },
            { value: UnitSystem.metric, label: t("metricMeters") },
            { value: UnitSystem.metricMM, label: t("metricMillimeters") },
        ],
        [t],
    );

    const performanceOptions = useMemo<PickerOption<string>[]>(
        () =>
            DEVICE_PROFILES.map((profile) => {
                const presetKey = getTierKey(profile.tier);
                return {
                    value: String(profile.tier),
                    label: t(`performancePresets.${presetKey}.name`),
                    description: t(`performancePresets.${presetKey}.description`),
                };
            }),
        [t],
    );

    const selectionResolutionOptions = useMemo<PickerOption<SelectionResolution>[]>(
        () =>
            SELECTION_RESOLUTION_OPTIONS.map((option) => ({
                value: option.value,
                label: t(`selectionResolutionOptions.${option.key}.name`),
                description: t(`selectionResolutionOptions.${option.key}.description`),
            })),
        [t],
    );

    const environmentOptions = useMemo<PickerOption<string>[]>(
        () =>
            environments.map((env) => ({
                value: env.url,
                label: t(`env.${env.name}`, { defaultValue: env.name }),
            })),
        [environments, t],
    );

    const getEnvironmentLabel = (url: string) => {
        const env = environments.find((e) => e.url === url);
        if (!env) {
            return url ? (url.split("/").filter(Boolean).at(-1) ?? url) : t("loading");
        }
        return t(`env.${env.name}`, { defaultValue: env.name });
    };

    // Handlers
    const handleUnitSystemChange = (value: string) => {
        const newSystem = value as UnitSystem;
        if (newSystem !== unitSystem) {
            dispatch(explorerActions.setUnitSystem(newSystem));
            saveToStorage(StorageKey.UnitSystem, newSystem);
        }
    };

    const handlePerformanceChange = (value: string) => {
        const tier = Number(value) as GPUTier;
        if (tier !== currentTier) {
            dispatch(renderActions.setDeviceProfile(getDeviceProfile(tier)));
        }
    };

    const handleSelectionResolutionChange = (value: string) => {
        const newResolution = value as SelectionResolution;
        if (newResolution !== selectionResolution) {
            dispatch(explorerActions.setSelectionResolution(newResolution));
            saveToStorage(StorageKey.SelectionResolution, newResolution);
        }
    };

    const handleEnvironmentChange = (value: string) => {
        if (value && value !== background.url) {
            dispatch(renderActions.setBackground({ url: value }));
            saveToStorage(StorageKey.Environment, value);
        }
    };

    const handleFramerateTargetChange = (value: string) => {
        if (!FRAMERATE_TARGET_OPTIONS.includes(value as FramerateTargetValue)) {
            return;
        }
        const parsed = Number(value);
        if (parsed !== deviceProfile.framerateTarget) {
            dispatch(renderActions.setDeviceProfile({ framerateTarget: parsed }));
            saveToStorage(StorageKey.FramerateTarget, String(parsed));
        }
    };

    const handleAntiAliasingToggle = () => {
        const next = !isAntiAliasingEnabled;
        dispatch(renderActions.setAdvanced({ msaa: { enabled: next } }));
        saveToStorage(StorageKey.AntiAliasing, String(next));
    };

    const handleMiniMapToggle = () => dispatch(persistFeatureToggle(FeatureToggle.MiniMapVisible, !isMiniMapVisible));
    const handleNavSphereToggle = () =>
        dispatch(persistFeatureToggle(FeatureToggle.AxisSphereVisible, !isAxisSphereVisible));
    const handleJoysticksToggle = () =>
        dispatch(persistFeatureToggle(FeatureToggle.JoysticksVisible, !isJoysticksVisible));

    const handleOpenModal = () => setModalOpen(true);
    const handleCloseModal = () => setModalOpen(false);

    const handleConfirmHomeViewUpdate = async () => {
        if (isUpdating || view?.activeController.kind === "panorama" || !companyId || !projectId) {
            return;
        }

        dispatch(explorerActions.setSnackbarMessage(null));

        setIsUpdating(true);
        try {
            const [viewpointPayload, revisions] = await Promise.all([
                getViewpoint({ createSnapshot: true, snapshotWidth: 3840, snapshotHeight: 2160, snapshotType: "jpeg" }),
                getBimModelRevisions({
                    companyId,
                    projectId,
                    sceneId,
                    sceneVersionId: procoreContext?.sceneVersionId,
                }),
            ]);

            const resolvedSceneVersionId = procoreContext?.sceneVersionId ?? sceneId;
            const revision =
                revisions.find((r) => r.current_platform_scene_version_id === resolvedSceneVersionId) ??
                revisions.find((r) => r.current_platform_scene_id === sceneId);

            if (!revision?.bim_model_id) {
                throw new Error("Could not resolve model id from revision");
            }

            const platformSceneId = revision.current_platform_scene_id;
            if (!platformSceneId) {
                throw new Error("Could not resolve platform scene id from revision");
            }

            if (!viewpointPayload.snapshot) {
                throw new Error("Failed to create snapshot for home viewpoint");
            }

            const existingHomeView = await getModelHomeView({
                companyId,
                projectId,
                modelId: revision.bim_model_id,
            });

            await saveModelHomeViewpoint({
                companyId,
                projectId,
                modelId: revision.bim_model_id,
                sceneId: platformSceneId,
                viewpoint: viewpointPayload,
                existingHomeView,
            });

            dispatch(renderActions.setInitialCamera(viewpointCameraToCadCamera(viewpointPayload.camera)));
            handleCloseModal();
            dispatch(explorerActions.setSnackbarMessage({ msg: t("homeViewUpdateSuccess"), closeAfter: 5000 }));
        } catch (e) {
            console.error("[Home View] Failed to update home view:", e);
            telemetry.trackCaughtException({
                source: "settingsContent.updateHomeView",
                category: "data",
                error: e,
                severity: "error",
            });
            dispatch(explorerActions.setSnackbarMessage({ msg: t("homeViewUpdateError"), closeAfter: 5000 }));
        } finally {
            setIsUpdating(false);
        }
    };

    const canUpdateHomeView = Boolean(view && companyId && projectId) && isOnline;
    const homeViewDisabledReason = !isOnline ? t("notAvailableOffline") : "";

    const updateHomeViewButton = (
        <Button
            variant="secondary"
            icon={<Home />}
            disabled={!canUpdateHomeView}
            onClick={handleOpenModal}
            data-testid="settings-update-home-view-button"
        >
            {t("updateHomeView")}
        </Button>
    );

    const homeViewSection = (
        <>
            <Box display="flex" flexDirection="column" gap="0.5rem">
                <FormLabel sx={{ fontWeight: 600, color: "text.primary" }}>{t("homeView")}</FormLabel>
                <Typography variant="body1" color="text.secondary">
                    {t("homeViewDescription")}
                </Typography>
                <Box>
                    {homeViewDisabledReason ? (
                        // Wrap in span so the tooltip can attach to a disabled element
                        <Tooltip title={homeViewDisabledReason} placement="top">
                            <span>{updateHomeViewButton}</span>
                        </Tooltip>
                    ) : (
                        updateHomeViewButton
                    )}
                </Box>
            </Box>

            <ConfirmModal
                open={modalOpen}
                headline={t("updateProjectHomeViewTitle")}
                onClose={handleCloseModal}
                width="sm"
            >
                <Modal.Body>
                    <Typography>{t("updateProjectHomeViewBody")}</Typography>
                </Modal.Body>
                <Modal.Footer>
                    <Modal.FooterButtons onClose={handleCloseModal}>
                        <Button variant="tertiary" onClick={handleCloseModal} disabled={isUpdating}>
                            {t("cancel")}
                        </Button>
                        <Button variant="primary" onClick={handleConfirmHomeViewUpdate} disabled={isUpdating}>
                            {isUpdating ? <Spinner size="sm" loading variant="button" /> : t("update")}
                        </Button>
                    </Modal.FooterButtons>
                </Modal.Footer>
            </ConfirmModal>
        </>
    );

    // Overlay picker helpers
    const closePickerOverlay = () => pickerOverlay?.setOverlay(null);

    const openPicker = (kind: PickerKind) => {
        if (!pickerOverlay) {
            return;
        }

        const wrapDone = (handler: (v: string) => void) => (v: string) => {
            handler(v);
            closePickerOverlay();
        };

        switch (kind) {
            case "unitSystem":
                pickerOverlay.setOverlay(
                    <SettingsPickerView
                        title={t("measurementSystem")}
                        options={unitSystemOptions}
                        initialValue={unitSystem}
                        onCancel={closePickerOverlay}
                        onDone={wrapDone(handleUnitSystemChange)}
                        testIdPrefix="settings-measurement-system"
                    />,
                );
                break;
            case "performance":
                pickerOverlay.setOverlay(
                    <SettingsPickerView
                        title={t("performance")}
                        options={performanceOptions}
                        initialValue={String(currentTier)}
                        onCancel={closePickerOverlay}
                        onDone={wrapDone(handlePerformanceChange)}
                        testIdPrefix="settings-performance"
                    />,
                );
                break;
            case "selectionResolution":
                pickerOverlay.setOverlay(
                    <SettingsPickerView
                        title={t("selectionResolution")}
                        options={selectionResolutionOptions}
                        initialValue={selectionResolution}
                        onCancel={closePickerOverlay}
                        onDone={wrapDone(handleSelectionResolutionChange)}
                        testIdPrefix="settings-selection-resolution"
                    />,
                );
                break;
        }
    };

    // Clear overlay when this component unmounts (e.g. different drawer opened)
    useEffect(() => {
        return () => {
            pickerOverlay?.setOverlay(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box display="flex" flexDirection="column" gap="1rem" padding="1rem">
            {/* Measurement System */}
            {useSlideInPicker ? (
                <PickerRow
                    label={t("measurementSystem")}
                    valueLabel={unitSystemOptions.find((opt) => opt.value === unitSystem)?.label ?? unitSystem}
                    onClick={() => openPicker("unitSystem")}
                    testId="settings-measurement-system-picker-row"
                />
            ) : (
                <FormControl fullWidth size="small">
                    <FormLabel sx={sectionLabelSx}>{t("measurementSystem")}</FormLabel>
                    <Select
                        value={unitSystem}
                        onChange={(e) => handleUnitSystemChange(e.target.value)}
                        size="small"
                        fullWidth
                        data-testid="settings-measurement-system-select"
                    >
                        {unitSystemOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            {/* Performance */}
            {useSlideInPicker ? (
                <PickerRow
                    label={t("performance")}
                    valueLabel={t(`performancePresets.${getTierKey(currentTier)}.name`)}
                    onClick={() => openPicker("performance")}
                    testId="settings-performance-picker-row"
                />
            ) : (
                <FormControl fullWidth size="small">
                    <FormLabel sx={sectionLabelSx}>{t("performance")}</FormLabel>
                    <Select
                        value={String(currentTier)}
                        onChange={(e) => handlePerformanceChange(String(e.target.value))}
                        size="small"
                        fullWidth
                        renderValue={(value) => performanceOptions.find((opt) => opt.value === value)?.label ?? ""}
                        data-testid="settings-performance-select"
                    >
                        {performanceOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                <Box>
                                    <Typography variant="body2">{option.label}</Typography>
                                    <Typography variant="body1" color="text.secondary">
                                        {option.description}
                                    </Typography>
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            {/* Selection Resolution */}
            {useSlideInPicker ? (
                <PickerRow
                    label={t("selectionResolution")}
                    valueLabel={
                        selectionResolutionOptions.find((opt) => opt.value === selectionResolution)?.label ?? ""
                    }
                    onClick={() => openPicker("selectionResolution")}
                    testId="settings-selection-resolution-picker-row"
                />
            ) : (
                <FormControl fullWidth size="small">
                    <FormLabel sx={sectionLabelSx}>{t("selectionResolution")}</FormLabel>
                    <Select
                        value={selectionResolution}
                        onChange={(e) => handleSelectionResolutionChange(e.target.value)}
                        size="small"
                        fullWidth
                        renderValue={(value) =>
                            selectionResolutionOptions.find((opt) => opt.value === value)?.label ?? ""
                        }
                        data-testid="settings-selection-resolution-select"
                    >
                        {selectionResolutionOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                <Box>
                                    <Typography variant="body2">{option.label}</Typography>
                                    <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: "normal" }}>
                                        {option.description}
                                    </Typography>
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            {/* Environment — desktop only */}
            {!useSlideInPicker && (
                <FormControl fullWidth size="small">
                    <FormLabel sx={sectionLabelSx}>{t("environment")}</FormLabel>
                    <Select
                        value={environmentOptions.some((opt) => opt.value === background.url) ? background.url : ""}
                        onChange={(e) => handleEnvironmentChange(e.target.value)}
                        size="small"
                        fullWidth
                        displayEmpty
                        disabled={environmentOptions.length === 0}
                        renderValue={(value) =>
                            value ? getEnvironmentLabel(value as string) : getEnvironmentLabel(background.url)
                        }
                        data-testid="settings-environment-select"
                    >
                        {environmentOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            {/* Target Frame Rate — desktop only */}
            {!useSlideInPicker && (
                <FormControl fullWidth size="small">
                    <FormLabel sx={sectionLabelSx}>{t("framerateTarget")}</FormLabel>
                    <Select
                        value={framerateTarget}
                        onChange={(e) => handleFramerateTargetChange(e.target.value)}
                        size="small"
                        fullWidth
                        data-testid="settings-framerate-target-select"
                    >
                        {FRAMERATE_TARGET_OPTIONS.map((value) => (
                            <MenuItem key={value} value={value}>
                                {t(`framerateTargetOptions.${value}`)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            {/* Home View — mobile only, admin-gated, above toggles */}
            {useSlideInPicker && isAdminScene && homeViewSection}

            {/* Show in Viewer toggles */}
            <MuiBox
                display="flex"
                flexDirection="column"
                gap="0.5rem"
                sx={{
                    "& label[class*='StyledSwitchContainer']": {
                        border: "1px solid transparent !important",
                        boxShadow: "none !important",
                        outline: "none !important",
                    },
                }}
            >
                <FormLabel sx={{ fontWeight: 600, color: "text.primary" }}>{t("showInViewer")}</FormLabel>

                {/* 2D Mini Map — always shown; disabled until a drawing is loaded */}
                <ToggleWithText
                    checked={isMiniMapVisible && miniMapAvailable}
                    onChange={handleMiniMapToggle}
                    label="miniMap"
                    disabled={!miniMapAvailable}
                />

                {/* Anti-Aliasing */}
                <ToggleWithText
                    checked={isAntiAliasingEnabled}
                    onChange={handleAntiAliasingToggle}
                    label="antiAliasing"
                />

                {/* Navigation Sphere — always shown on all form factors */}
                <ToggleWithText
                    checked={isAxisSphereVisible}
                    onChange={handleNavSphereToggle}
                    label="navigationSphere"
                />

                {/* Joysticks — mobile only */}
                {isProcoreMobileDesign && (
                    <ToggleWithText checked={isJoysticksVisible} onChange={handleJoysticksToggle} label="joysticks" />
                )}
            </MuiBox>

            {/* Home View — desktop only, feature-toggle-gated */}
            {!useSlideInPicker && isUpdateHomeViewEnabled && homeViewSection}

            {/* Model Download: mobile-only, host opt-in via initOptions.procore.enableFullSync. */}
            {isProcoreMobileDesign && procoreContext?.enableFullSync && parentSceneId && (
                <ModelDownloadSection parentSceneId={parentSceneId} />
            )}
        </Box>
    );
}
